import { ProcessTypes } from "@/types";
import { processService } from "@/services/process";
import {
  incomingService,
  type GetAllApplicationsOptions,
  type GetApplicationsForProcessOptions,
} from "@/services/process/incoming.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type ApplicationType = "pending" | "completed" | "disabled" | "processed";

export const useGetApplications = (
  type: ApplicationType,
  isAdmin: boolean = false,
  options?: GetAllApplicationsOptions
) =>
  useQuery<any, Error, any>({
    queryKey: [`${type}-applications`, isAdmin, options?.page, options?.limit, options?.search],
    queryFn: () => incomingService.getAllApplications(type, isAdmin, options),
  });

export const useGetApplicationsForProcess = (
  processId: string,
  type: ApplicationType,
  isAdmin: boolean = false,
  status?: string,
  options?: GetApplicationsForProcessOptions
) =>
  useQuery<any, Error, any>({
    queryKey: [`${type}-applications-process`, processId, isAdmin, status, options?.page, options?.limit, options?.search],
    queryFn: () =>
      incomingService.getApplicationsForProcess(
        processId,
        type,
        isAdmin,
        status,
        options
      ),
    enabled: !!processId,
  });

// 3. Get single application details
export const useGetSingleApplication = (applicantProcessId: string, type?: 'pending' | 'completed' | 'disabled' | 'processed') =>
  useQuery<any, Error, any>({
    queryKey: ["single-application", applicantProcessId, type],
    queryFn: () => incomingService.getSingleApplication(applicantProcessId, type),
    enabled: !!applicantProcessId,
  });

// Return the whole application to the applicant, with a reason
export const useReturnToApplicant = () => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { applicantProcessId: string; comment: string }>({
    mutationFn: processService.returnToApplicant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["single-application"] });
    },
  });
};

// Sendback Process Mutation
export const useSendbackProcess = () => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, ProcessTypes>({
    mutationFn: processService.sendbackProcess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["single-application"] });
    },
  });
};
